import { useState, useEffect } from "react";
import { Header, BottomNav } from "../components/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Package, DollarSign, TrendingUp, Edit2, Trash2, Link as LinkIcon, Search, Landmark } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const BANK_ACCOUNTS = ["Banco General", "Banitsmo", "BAC", "Davivienda", "Indii"];

const InventoryPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [accountStats, setAccountStats] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("Todas");
  const [fundInput, setFundInput] = useState("");
  const [savingFund, setSavingFund] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sellDialogOpen, setSellDialogOpen] = useState(false);
  const [sellItem, setSellItem] = useState(null);
  const [sellPrice, setSellPrice] = useState("");
  
  // Form states
  const [editingItem, setEditingItem] = useState(null);
  const [newItem, setNewItem] = useState({ name: "", product_amount: "", shipping_cost: "", notes: "", bank_account: "" });
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.is_admin && token) {
      fetchInventory();
      fetchStats();
      fetchAccountStats();
    }
  }, [user, token]);

  const fetchInventory = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/inventory`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setItems(response.data);
    } catch (error) {
      console.error("Error fetching inventory:", error);
      const errorMsg = error.response?.data?.detail || error.message;
      console.error("Inventory error detail:", errorMsg);
      toast.error(`Error al cargar inventario: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/inventory/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchAccountStats = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/inventory/by-account`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setAccountStats(response.data.accounts || []);
    } catch (error) {
      console.error("Error fetching account stats:", error);
    }
  };

  const handleSaveFund = async (bankAccount) => {
    if (fundInput === "" || isNaN(parseFloat(fundInput))) {
      toast.error("Ingresa un monto válido");
      return;
    }
    setSavingFund(true);
    try {
      await axios.put(`${API}/inventory/account-funds/${encodeURIComponent(bankAccount)}`, {
        initial_fund: parseFloat(fundInput)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Fondo inicial actualizado");
      fetchAccountStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar el fondo");
    } finally {
      setSavingFund(false);
    }
  };

  const handleMarkSold = async () => {
    if (!sellItem || !sellPrice || isNaN(parseFloat(sellPrice))) {
      toast.error("Ingresa un valor de venta válido");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inventory/${sellItem.inventory_id}/mark-sold`,
        { sale_price: parseFloat(sellPrice) },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      toast.success("Venta registrada en el inventario");
      setSellDialogOpen(false);
      setSellItem(null);
      setSellPrice("");
      fetchInventory();
      fetchStats();
      fetchAccountStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al registrar venta");
    } finally {
      setSubmitting(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-products`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      // Filter only active products not yet linked to inventory
      const activeProducts = response.data.filter(p => !p.is_sold && p.status !== "sold");
      setProducts(activeProducts);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleCreate = async () => {
    if (!newItem.name || !newItem.product_amount) {
      toast.error("Completa los campos requeridos");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/inventory`, {
        name: newItem.name,
        product_amount: parseFloat(newItem.product_amount),
        shipping_cost: parseFloat(newItem.shipping_cost || 0),
        notes: newItem.notes || null,
        bank_account: newItem.bank_account || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Item agregado al inventario");
      setCreateDialogOpen(false);
      setNewItem({ name: "", product_amount: "", shipping_cost: "", notes: "", bank_account: "" });
      fetchInventory();
      fetchStats();
      fetchAccountStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear item");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingItem) return;
    
    setSubmitting(true);
    try {
      await axios.put(`${API}/inventory/${editingItem.inventory_id}`, {
        name: editingItem.name,
        product_amount: parseFloat(editingItem.product_amount),
        shipping_cost: parseFloat(editingItem.shipping_cost || 0),
        notes: editingItem.notes || null,
        bank_account: editingItem.bank_account || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Item actualizado");
      setEditDialogOpen(false);
      setEditingItem(null);
      fetchInventory();
      fetchStats();
      fetchAccountStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!editingItem) return;
    
    setSubmitting(true);
    try {
      await axios.delete(`${API}/inventory/${editingItem.inventory_id}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Item eliminado");
      setDeleteConfirmOpen(false);
      setEditingItem(null);
      fetchInventory();
      fetchStats();
      fetchAccountStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkProduct = async () => {
    if (!editingItem || !selectedProductId) {
      toast.error("Selecciona un producto");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/inventory/${editingItem.inventory_id}/link-product/${selectedProductId}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      
      toast.success("Producto enlazado al inventario");
      setLinkDialogOpen(false);
      setEditingItem(null);
      setSelectedProductId("");
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enlazar");
    } finally {
      setSubmitting(false);
    }
  };

  const openLinkDialog = (item) => {
    setEditingItem(item);
    fetchProducts();
    setLinkDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "in_stock":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">En Stock</span>;
      case "listed":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">En Venta</span>;
      case "sold":
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Vendido</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">{status}</span>;
    }
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (selectedAccount === "Todas" || (item.bank_account || "Sin cuenta") === selectedAccount)
  );

  if (!user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <p className="text-[#64748B]">Acceso solo para administradores</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="p-2 hover:bg-[#F1F5F9] rounded-full">
              <ArrowLeft size={20} className="text-[#64748B]" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-[#0F172A]">Inventario Privado</h1>
              <p className="text-sm text-[#64748B]">Gestiona tu stock y ganancias</p>
            </div>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#0F172A] hover:bg-[#1e293b] rounded-full"
            data-testid="add-inventory-btn"
          >
            <Plus size={18} className="mr-2" />
            Agregar
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
              <div className="flex items-center gap-2 mb-2">
                <Package size={18} className="text-blue-500" />
                <span className="text-xs text-[#64748B]">En Stock</span>
              </div>
              <p className="text-2xl font-bold text-[#0F172A]">{stats.in_stock?.count || 0}</p>
              <p className="text-xs text-[#64748B]">${(stats.in_stock?.total_purchase || 0).toLocaleString()} invertido</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={18} className="text-amber-500" />
                <span className="text-xs text-[#64748B]">En Venta</span>
              </div>
              <p className="text-2xl font-bold text-[#0F172A]">{stats.listed?.count || 0}</p>
              <p className="text-xs text-[#64748B]">${(stats.listed?.total_purchase || 0).toLocaleString()} invertido</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-[#E2E8F0]">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-green-500" />
                <span className="text-xs text-[#64748B]">Vendidos</span>
              </div>
              <p className="text-2xl font-bold text-[#0F172A]">{stats.sold?.count || 0}</p>
              <p className="text-xs text-[#64748B]">${(stats.sold?.total_sale || 0).toLocaleString()} vendido</p>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-[#E2E8F0] bg-gradient-to-br from-green-50 to-emerald-50">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={18} className="text-emerald-600" />
                <span className="text-xs text-emerald-700 font-medium">Ganancia Total</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600">${(stats.sold?.total_profit || 0).toLocaleString()}</p>
              <p className="text-xs text-emerald-600">{stats.totals?.total_items || 0} items totales</p>
            </div>
          </div>
        )}

        {/* Bank Account Filter + History */}
        <div className="mb-6">
          <p className="text-xs font-semibold text-[#64748B] mb-2 flex items-center gap-1.5">
            <Landmark size={14} /> Cuenta bancaria
          </p>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="inventory-account-filters">
            {["Todas", ...BANK_ACCOUNTS, "Sin cuenta"].map((acc) => (
              <button
                key={acc}
                onClick={() => {
                  setSelectedAccount(acc);
                  const accData = accountStats.find(a => a.bank_account === acc);
                  setFundInput(accData?.initial_fund != null ? String(accData.initial_fund) : "");
                }}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-colors whitespace-nowrap ${
                  selectedAccount === acc
                    ? "bg-[#0F172A] text-white border-[#0F172A]"
                    : "bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0F172A]"
                }`}
                data-testid={`account-filter-${acc.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {acc}
              </button>
            ))}
          </div>

          {selectedAccount !== "Todas" && (() => {
            const accData = accountStats.find(a => a.bank_account === selectedAccount);
            const hasFund = selectedAccount !== "Sin cuenta";
            return (
              <div className="mt-3 bg-white rounded-xl border border-[#E2E8F0] p-4 space-y-4" data-testid="account-history-card">
                {hasFund && (
                  <div className="flex flex-wrap items-end gap-2 pb-3 border-b border-[#E2E8F0]">
                    <div className="flex-1 min-w-[160px]">
                      <Label htmlFor="fund-input" className="text-xs">Fondo inicial de {selectedAccount} ($)</Label>
                      <Input
                        id="fund-input"
                        type="number"
                        value={fundInput}
                        onChange={(e) => setFundInput(e.target.value)}
                        placeholder="0.00"
                        data-testid="account-fund-input"
                      />
                    </div>
                    <Button onClick={() => handleSaveFund(selectedAccount)} disabled={savingFund} size="sm" data-testid="account-fund-save-btn">
                      Guardar fondo
                    </Button>
                  </div>
                )}

                {hasFund && accData?.over_capacity && (
                  <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-semibold rounded-lg px-3 py-2" data-testid="account-over-capacity-warning">
                    ⚠ Esta cuenta superó su capacidad: hay más dinero en stock del que el fondo inicial permite.
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  <div>
                    <p className="text-xs text-[#94A3B8]">Items</p>
                    <p className="text-lg font-bold text-[#0F172A]" data-testid="account-history-count">{accData?.count || 0}</p>
                  </div>
                  {hasFund && (
                    <div>
                      <p className="text-xs text-[#94A3B8]">Disponible</p>
                      <p className={`text-lg font-bold ${accData?.over_capacity ? "text-red-600" : "text-[#0F172A]"}`} data-testid="account-history-available">
                        ${(accData?.available ?? 0).toLocaleString()}
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-[#94A3B8]">Monto usado (stock)</p>
                    <p className="text-lg font-bold text-[#0F172A]" data-testid="account-history-used">${(accData?.used_amount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Vendido</p>
                    <p className="text-lg font-bold text-[#0F172A]" data-testid="account-history-sold">${(accData?.total_sale || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Ganancia</p>
                    <p className="text-lg font-bold text-emerald-600" data-testid="account-history-profit">${(accData?.total_profit || 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input
            placeholder="Buscar en inventario..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-full border-[#E2E8F0]"
            data-testid="inventory-search"
          />
        </div>

        {/* Inventory List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="bg-white rounded-xl p-8 text-center border border-[#E2E8F0]">
            <Package size={48} className="mx-auto text-[#CBD5E1] mb-4" />
            <p className="text-[#64748B]">
              {searchTerm ? "No se encontraron resultados" : "No hay items en el inventario"}
            </p>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              variant="outline"
              className="mt-4 rounded-full"
            >
              Agregar primer item
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <div 
                key={item.inventory_id} 
                className="bg-white rounded-xl p-4 border border-[#E2E8F0] hover:shadow-md transition-shadow"
                data-testid={`inventory-item-${item.inventory_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-[#0F172A]">{item.name}</h3>
                      {getStatusBadge(item.status)}
                      {item.bank_account && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600 flex items-center gap-1" data-testid={`inventory-item-account-${item.inventory_id}`}>
                          <Landmark size={11} /> {item.bank_account}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-[#94A3B8] text-xs">Compra</p>
                        <p className="font-medium text-[#64748B]">${item.purchase_price?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-[#94A3B8] text-xs">Venta</p>
                        <p className="font-medium text-[#0F172A]">
                          {item.sale_price ? `$${item.sale_price.toLocaleString()}` : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#94A3B8] text-xs">Ganancia</p>
                        <p className={`font-bold ${item.profit > 0 ? 'text-green-600' : item.profit < 0 ? 'text-red-600' : 'text-[#64748B]'}`}>
                          {item.profit !== null ? `$${item.profit.toLocaleString()}` : "-"}
                        </p>
                      </div>
                    </div>
                    
                    {item.notes && (
                      <p className="text-xs text-[#94A3B8] mt-2">{item.notes}</p>
                    )}
                    
                    {item.linked_product_id && (
                      <Link 
                        to={`/product/${item.linked_product_id}`}
                        className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                      >
                        <LinkIcon size={12} />
                        Ver producto enlazado
                      </Link>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-1 ml-4">
                    {item.status !== "sold" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSellItem(item);
                          setSellPrice(item.sale_price ? item.sale_price.toString() : "");
                          setSellDialogOpen(true);
                        }}
                        className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        title="Marcar como vendido (venta manual)"
                        data-testid={`inventory-sell-btn-${item.inventory_id}`}
                      >
                        <DollarSign size={16} />
                      </Button>
                    )}
                    {item.status === "in_stock" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openLinkDialog(item)}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="Enlazar a producto"
                      >
                        <LinkIcon size={16} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingItem({
                          ...item,
                          product_amount: (item.product_amount ?? item.purchase_price).toString(),
                          shipping_cost: (item.shipping_cost ?? 0).toString()
                        });
                        setEditDialogOpen(true);
                      }}
                      className="text-[#64748B] hover:text-[#0F172A]"
                    >
                      <Edit2 size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditingItem(item);
                        setDeleteConfirmOpen(true);
                      }}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Manual Sale Dialog */}
      <Dialog open={sellDialogOpen} onOpenChange={setSellDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Registrar venta manual</DialogTitle>
            <DialogDescription>
              Ingresa el valor real de venta de "{sellItem?.name}" (ej. si se vendió fuera de la web) para mantener tus ingresos exactos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="sell-price">Valor de venta ($) *</Label>
              <Input
                id="sell-price"
                type="number"
                min="0"
                step="0.01"
                value={sellPrice}
                onChange={(e) => setSellPrice(e.target.value)}
                placeholder="0.00"
                className="mt-1"
                data-testid="inventory-sell-price-input"
              />
              {sellItem && sellPrice && (
                <p className={`text-xs mt-2 font-medium ${parseFloat(sellPrice) - sellItem.purchase_price >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                  Ganancia: ${(parseFloat(sellPrice) - sellItem.purchase_price).toLocaleString()}
                </p>
              )}
              {sellItem?.linked_product_id && (
                <p className="text-xs text-[#94A3B8] mt-2">El producto publicado enlazado también se marcará como vendido.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSellDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button
              onClick={handleMarkSold}
              disabled={submitting || !sellPrice}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="inventory-sell-confirm-btn"
            >
              {submitting ? "Guardando..." : "Registrar venta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar al Inventario</DialogTitle>
            <DialogDescription>Registra un nuevo producto en tu inventario privado</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="name">Nombre del producto *</Label>
              <Input
                id="name"
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                placeholder="Ej: iPhone 15 Pro Max"
                data-testid="inventory-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="product_amount">Monto del producto ($) *</Label>
                <Input
                  id="product_amount"
                  type="number"
                  value={newItem.product_amount}
                  onChange={(e) => setNewItem({...newItem, product_amount: e.target.value})}
                  placeholder="0.00"
                  data-testid="inventory-price-input"
                />
              </div>
              <div>
                <Label htmlFor="shipping_cost">Costo de envío ($)</Label>
                <Input
                  id="shipping_cost"
                  type="number"
                  value={newItem.shipping_cost}
                  onChange={(e) => setNewItem({...newItem, shipping_cost: e.target.value})}
                  placeholder="0.00"
                  data-testid="inventory-shipping-input"
                />
              </div>
            </div>
            <p className="text-xs text-[#64748B]" data-testid="inventory-total-preview">
              Total de compra: <span className="font-semibold text-[#0F172A]">
                ${((parseFloat(newItem.product_amount) || 0) + (parseFloat(newItem.shipping_cost) || 0)).toLocaleString()}
              </span>
            </p>
            <div>
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Input
                id="notes"
                value={newItem.notes}
                onChange={(e) => setNewItem({...newItem, notes: e.target.value})}
                placeholder="Ej: Comprado en Amazon, IMEI: xxx"
              />
            </div>
            <div>
              <Label htmlFor="bank_account">Cuenta bancaria usada (opcional)</Label>
              <Select value={newItem.bank_account} onValueChange={(v) => setNewItem({...newItem, bank_account: v})}>
                <SelectTrigger id="bank_account" data-testid="inventory-bank-account-select">
                  <SelectValue placeholder="Selecciona una cuenta" />
                </SelectTrigger>
                <SelectContent>
                  {BANK_ACCOUNTS.map((acc) => (
                    <SelectItem key={acc} value={acc} data-testid={`bank-account-option-${acc.replace(/\s+/g, "-").toLowerCase()}`}>{acc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleCreate} 
              disabled={submitting}
              className="bg-[#0F172A] hover:bg-[#1e293b]"
              data-testid="inventory-save-btn"
            >
              {submitting ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Item</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-name">Nombre del producto</Label>
                <Input
                  id="edit-name"
                  value={editingItem.name}
                  onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="edit-price">Monto del producto ($)</Label>
                  <Input
                    id="edit-price"
                    type="number"
                    value={editingItem.product_amount}
                    onChange={(e) => setEditingItem({...editingItem, product_amount: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="edit-shipping">Costo de envío ($)</Label>
                  <Input
                    id="edit-shipping"
                    type="number"
                    value={editingItem.shipping_cost}
                    onChange={(e) => setEditingItem({...editingItem, shipping_cost: e.target.value})}
                  />
                </div>
              </div>
              <p className="text-xs text-[#64748B]">
                Total de compra: <span className="font-semibold text-[#0F172A]">
                  ${((parseFloat(editingItem.product_amount) || 0) + (parseFloat(editingItem.shipping_cost) || 0)).toLocaleString()}
                </span>
              </p>
              <div>
                <Label htmlFor="edit-notes">Notas</Label>
                <Input
                  id="edit-notes"
                  value={editingItem.notes || ""}
                  onChange={(e) => setEditingItem({...editingItem, notes: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="edit-bank-account">Cuenta bancaria usada</Label>
                <Select value={editingItem.bank_account || ""} onValueChange={(v) => setEditingItem({...editingItem, bank_account: v})}>
                  <SelectTrigger id="edit-bank-account" data-testid="edit-inventory-bank-account-select">
                    <SelectValue placeholder="Selecciona una cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {BANK_ACCOUNTS.map((acc) => (
                      <SelectItem key={acc} value={acc}>{acc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleUpdate} 
              disabled={submitting}
              className="bg-[#0F172A] hover:bg-[#1e293b]"
            >
              {submitting ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Product Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Enlazar a Producto</DialogTitle>
            <DialogDescription>
              Selecciona el producto de venta para enlazar con "{editingItem?.name}"
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {products.length === 0 ? (
              <p className="text-center text-[#64748B] py-4">No hay productos activos para enlazar</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {products.map((product) => (
                  <div
                    key={product.product_id}
                    onClick={() => setSelectedProductId(product.product_id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedProductId === product.product_id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-[#E2E8F0] hover:bg-[#F8FAFC]'
                    }`}
                  >
                    <p className="font-medium text-sm text-[#0F172A]">{product.title}</p>
                    <p className="text-xs text-[#64748B]">${product.price?.toLocaleString()} - {product.sale_type === 'auction' ? 'Subasta' : 'Venta directa'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleLinkProduct} 
              disabled={submitting || !selectedProductId}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? "Enlazando..." : "Enlazar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Eliminar Item</DialogTitle>
            <DialogDescription>
              ¿Estás seguro de eliminar "{editingItem?.name}" del inventario? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button 
              onClick={handleDelete} 
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {submitting ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default InventoryPage;
